import {
  IBug,
  IFarmInputProduct,
  IFarmInputProductInput,
  IFarmInputProductSubCategory,
  IFarmInputProductSubSubCategory,
  IProvince,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {apiUploadFile} from 'constants/Api';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Skeleton} from '@material-ui/lab';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PublishIcon from '@material-ui/icons/Publish';
import {CalculatePriceDialog} from 'components/ProductForm/CalculatePriceDialog';
import {CalculatePriceFormSubmission} from 'components/ProductForm/CalculatePriceForm';
import {ProductReadOnlyDetails} from 'components/ProductForm/ProductReadOnlyDetails';

const DEFAULT_MEASUREMENTS = ['L', 'KG', 'EA', 'KL', 'PER', 'AE', 'TONS'];

export function ProductForm(props: {
  user: ISupplierUserProfile;
  product?: IFarmInputProduct;
  subCategories: IFarmInputProductSubCategory[];
  subSubCategories: IFarmInputProductSubSubCategory[];
  isLoading: boolean;
  isLoadingCategories: boolean;
  isLoadingProvinces: boolean;
  provinces: IProvince[];
  isLoadingBugs: boolean;
  bugs: IBug[];
  allProducts: IFarmInputProduct[];
  submitButtonTitle: string;
  onSubmit: (product: IFarmInputProductInput) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  const {
    isLoading,
    isLoadingCategories,
    subCategories,
    subSubCategories,
    isLoadingProvinces,
    provinces,
    isLoadingBugs,
    bugs,
  } = props;

  type Submission = IFarmInputProductInput;
  const {handleSubmit, control, setValue} = useForm<Submission>({
    defaultValues: {
      category: '',
      ...props?.product,
      mainInputSupplierId: props.user.mainSupplier?.id,
      brandSupplierId: props.user.brand?.id,
      provincialRestrictions:
        props?.product?.provincialRestrictions.map((province) => province.id) ||
        [],
      relatedProducts:
        props?.product?.relatedProducts?.map((product) => product.id) || [],
      bugs: props?.product?.bugs.map((bug) => bug.id) || [],
    },
  });

  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingLabel, setIsUploadingLabel] = useState(false);
  const [showPriceDialog, setShowPriceDialog] = useState(false);

  const uploadImage = (event: any) => {
    const image = event?.target?.files?.[0];
    if (!image) return;

    const fileExtension = image.name.split('.').pop()?.substr(0, 4);
    const fileKey = `${new Date().getTime()}_${
      props.user.userId
    }_product_.${fileExtension}`;

    setIsUploadingImage(true);
    return apiUploadFile(image, fileKey)
      .catch((error) => {
        alert('Error uploading image. Please try again later');
        console.error(error);
        throw error;
      })
      .finally(() => setIsUploadingImage(false));
  };

  const uploadLabel = (event: any) => {
    if (props.readonly) return;
    const labelDoc = event?.target?.files?.[0];
    if (!labelDoc) return;

    const fileExtension = labelDoc.name.split('.').pop()?.substr(0, 4);
    const fileKey = `product-labels/${new Date().getTime()}.${fileExtension}`;

    setIsUploadingLabel(true);
    return apiUploadFile(labelDoc, fileKey)
      .catch((error) => {
        alert('Error uploading label. Please try again later');
        console.error(error);
        throw error;
      })
      .finally(() => setIsUploadingLabel(false));
  };

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      ...submission,
      price: parseFloat(submission.price as any),
      costPrice: parseFloat(submission.costPrice as any) || undefined,
    });
  };

  const findSubCategory = (id: any) => {
    return subCategories?.find((category) => category.id === id);
  };

  const findSubSubCategory = (id: any) => {
    return subSubCategories?.find((category) => category.id === id);
  };

  const findProvincesByIds = (ids: string[]): IProvince[] => {
    return ids
      .map((id) => provinces.find((province) => province.id === id))
      .filter((province) => !!province) as IProvince[];
  };

  const findBugsByIds = (ids: (string | number)[]): IBug[] => {
    return ids
      .map((id) => bugs.find((bug) => bug.id === id))
      .filter((bug) => !!bug) as IBug[];
  };

  const findProductsByIds = (ids: (string | number)[]): IFarmInputProduct[] => {
    return ids
      .map((id) => props.allProducts.find((product) => product.id === id))
      .filter((province) => !!province) as IFarmInputProduct[];
  };

  const handlePriceCalculation = (
    calculation: CalculatePriceFormSubmission,
  ) => {
    setValue('price', calculation.sellingPrice, {shouldTouch: true});
    setValue('costPrice', calculation.costPrice, {shouldTouch: true});
    setShowPriceDialog(false);
  };

  if (props.readonly) {
    return (
      <ProductReadOnlyDetails
        product={props.product}
        subCategories={subCategories}
        subSubCategories={subSubCategories}
        onClose={props.onCancel}
      />
    );
  }

  return (
    <div className="generic-form-container product-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Box className="photo-upload-container form-section">
            <Controller
              name="image"
              control={control}
              render={({field: {onChange, value}, fieldState: {error}}) => (
                <IconButton
                  component="label"
                  className="avatar"
                  size="medium"
                  disabled={props.readonly || isUploadingImage}
                >
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      const uploadImageFunc = uploadImage(event);
                      if (uploadImageFunc) {
                        uploadImageFunc.then(onChange);
                      }
                    }}
                  />
                  <div className="avatar-container">
                    {isUploadingImage ? (
                      <CircularProgress
                        color={'primary'}
                        size={20}
                        thickness={6}
                        className={'avatar'}
                      />
                    ) : (
                      <Avatar src={value} className="avatar avatar-border" />
                    )}
                  </div>
                </IconButton>
              )}
            />
            {!props.readonly && (
              <Controller
                name="image"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Button
                    component="label"
                    disabled={isUploadingImage}
                    size="small"
                    fullWidth
                  >
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => uploadImage(event)?.then(onChange)}
                    />
                    Upload Image
                  </Button>
                )}
              />
            )}
          </Box>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12} style={{marginTop: '-0.5rem'}}>
              <Controller
                name="isActive"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={(event) => onChange(event.target.checked)}
                        disabled={props.readonly || props.isLoading}
                      />
                    }
                    label="Active"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Name required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="tradeName"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Trade Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="stockCode"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Stock Code"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Stock Code required',
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Description
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input--multiline"
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <div className="price-title-container">
                <Typography variant={'subtitle2'} gutterBottom>
                  Pricing
                </Typography>
                {!props.readonly && (
                  <Button
                    variant="text"
                    onClick={() => setShowPriceDialog(true)}
                    size="small"
                    color={'primary'}
                  >
                    Calculate Price
                  </Button>
                )}
              </div>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="price"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Selling Price"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Selling price required',
                  validate: (value) =>
                    !isNaN(value!) || 'Selling price should be a number',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="costPrice"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Cost Price (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  validate: (value) =>
                    value === undefined ||
                    !isNaN(value!) ||
                    'Cost price should be a number',
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Dimensions
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="size"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Size"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Size required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="measurement"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Autocomplete
                    id="measurements"
                    disableClearable
                    freeSolo
                    autoSelect
                    options={DEFAULT_MEASUREMENTS}
                    disabled={props.readonly || isLoading}
                    getOptionLabel={(measurement: string) => measurement}
                    size="small"
                    value={value}
                    onChange={(event, measurement) => onChange(measurement)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="input"
                        label="Unit"
                        variant="outlined"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={isLoading}
                        size="medium"
                      />
                    )}
                  />
                )}
                rules={{
                  required: 'Measurement required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="lengthCm"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Length (cm)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="widthCm"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Width (cm)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="heightCm"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Height (cm)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="weightKg"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Weight (kg)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(value) =>
                      onChange(parseFloat(value.target.value))
                    }
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Other Information
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="activeIngredient"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Active Ingredient"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              {isLoadingCategories && (
                <Skeleton variant="rect" height={54} className="input" />
              )}
              {!isLoadingCategories && (
                <Controller
                  name="subCategoryId"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      id="categories"
                      options={subSubCategories || []}
                      disabled={props.readonly || isLoading}
                      getOptionLabel={(
                        category: IFarmInputProductSubSubCategory,
                      ) => category.name}
                      groupBy={(category) =>
                        findSubCategory(category.subCategoryId)?.description ||
                        ''
                      }
                      size="small"
                      value={findSubSubCategory(value)}
                      onChange={(event, category) => {
                        const categoryCode =
                          findSubCategory(category?.subCategoryId)?.code || '';
                        setValue('category', categoryCode);
                        setValue('subCategoryName', category?.name);
                        onChange(category?.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input"
                          label="Category"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error ? error.message : null}
                          disabled={
                            props.readonly || isLoadingCategories || isLoading
                          }
                          size="medium"
                        />
                      )}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="labelLink"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Label"
                    variant="outlined"
                    fullWidth
                    value={value}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={true}
                    InputProps={{
                      endAdornment: (
                        <>
                          {value && (
                            <InputAdornment position="end">
                              <IconButton
                                disabled={isUploadingLabel}
                                href={value}
                                target="_blank"
                                title="Open Label Document"
                                size="small"
                              >
                                <OpenInNewIcon />
                              </IconButton>
                            </InputAdornment>
                          )}
                          <InputAdornment position="end">
                            <IconButton
                              disabled={props.readonly || isUploadingLabel}
                              title="Upload Label Document"
                              size="small"
                              component="label" // important for input upload to work
                            >
                              <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                onChange={(event) =>
                                  uploadLabel(event)?.then(onChange)
                                }
                              />
                              {isUploadingLabel ? (
                                <CircularProgress
                                  color={'primary'}
                                  size={20}
                                  thickness={6}
                                  className={'avatar'}
                                />
                              ) : (
                                <PublishIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {isLoadingProvinces && (
                <Skeleton variant="rect" height={54} className="input" />
              )}
              {!isLoadingProvinces && (
                <Controller
                  name="provincialRestrictions"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      multiple
                      value={findProvincesByIds(value)}
                      options={provinces}
                      disabled={props.readonly || isLoading}
                      getOptionLabel={(province: IProvince) => province.name}
                      renderTags={(value: IProvince[], getTagProps) =>
                        value.map((province: IProvince, index: number) => (
                          <Chip
                            key={province.id}
                            variant="outlined"
                            label={province.name}
                            {...getTagProps({index})}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Provincial Restriction (Optional)"
                        />
                      )}
                      onChange={(event, provinces: IProvince[]) => {
                        onChange(provinces.map((province) => province.id));
                      }}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="relatedProducts"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Autocomplete
                    multiple
                    value={findProductsByIds(value)}
                    options={props.allProducts.filter(
                      (product) => product.id !== props.product?.id,
                    )}
                    disabled={props.readonly || isLoading}
                    getOptionLabel={(product: IFarmInputProduct) =>
                      product.name
                    }
                    renderTags={(value: IFarmInputProduct[], getTagProps) =>
                      value.map((product: IFarmInputProduct, index: number) => (
                        <Chip
                          key={product.id}
                          variant="outlined"
                          label={product.name}
                          {...getTagProps({index})}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Related Products (Optional)"
                      />
                    )}
                    onChange={(event, products: IFarmInputProduct[]) => {
                      onChange(products.map((product) => product.id));
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {isLoadingBugs && (
                <Skeleton variant="rect" height={54} className="input" />
              )}
              {!isLoadingBugs && (
                <Controller
                  name="bugs"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      multiple
                      value={findBugsByIds(value)}
                      options={bugs}
                      disabled={props.readonly || isLoading}
                      getOptionLabel={(bug: IBug) => bug.name}
                      renderTags={(value: IBug[], getTagProps) =>
                        value.map((bug: IBug, index: number) => (
                          <Chip
                            key={bug.id}
                            variant="outlined"
                            label={bug.name}
                            {...getTagProps({index})}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Bugs (Optional)"
                        />
                      )}
                      onChange={(event, bugs: IBug[]) => {
                        onChange(bugs.map((bug) => bug.id));
                      }}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading || props.isLoadingCategories}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading || props.isLoadingCategories}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
      <CalculatePriceDialog
        user={props.user}
        open={showPriceDialog}
        onClose={() => setShowPriceDialog(false)}
        onSubmit={handlePriceCalculation}
      />
    </div>
  );
}
